import { useRef, useState } from 'react';
import api from '../../services/api';
import { FormatMoney } from '../../utils/format';

export default function BlingOrderLinkPanel({ order, canEdit, onChanged }) {
    const [opened, setOpened] = useState(false);
    const [query, setQuery] = useState('');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const busyRef = useRef(false);
    const orderId = order.external_order_id;
    if (!canEdit) {
        return null;
    }
    const button = 'border border-mono-300 px-2 py-1 text-xs disabled:opacity-50 dark:border-mono-700';
    const act = async (operation) => {
        if (busyRef.current) {
            return;
        }
        busyRef.current = true;
        setBusy(true);
        setError('');
        try {
            await operation();
        } catch (err) {
            setError(err.message || 'Não foi possível consultar o Bling');
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };
    const search = (manual) =>
        act(async () => {
            const data = await api.searchPedidoBlingOrder(orderId, manual ? { query: query.trim() } : {});
            setResult(data);
            if (data.link) {
                await onChanged();
            }
        });
    const link = (id) =>
        act(async () => {
            await api.linkPedidoBlingOrder(orderId, { bling_order_id: id });
            setResult(null);
            setOpened(false);
            await onChanged();
        });
    return (
        <section className="border border-mono-200 bg-white px-4 py-2 dark:border-mono-800 dark:bg-mono-950">
            <button
                type="button"
                onClick={() => setOpened(!opened)}
                aria-expanded={opened}
                className="text-xs font-semibold underline"
            >
                {order.bling_order_id ? 'Alterar vínculo com Bling' : 'Vincular pedido ao Bling'}
            </button>
            {opened && (
                <div className="mt-3 flex flex-col gap-3">
                    <p className="text-xs text-mono-500">
                        Busque pelo pedido do canal ou pack. Uma correspondência única é vinculada automaticamente;
                        resultados ambíguos precisam de seleção.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <button type="button" className={button} disabled={busy} onClick={() => search(false)}>
                            Buscar correspondência
                        </button>
                        <form
                            className="flex items-center gap-2"
                            onSubmit={(event) => {
                                event.preventDefault();
                                search(true);
                            }}
                        >
                            <input
                                aria-label="Número do pedido Bling"
                                placeholder="Número do pedido Bling"
                                inputMode="numeric"
                                pattern="[1-9][0-9]{0,39}"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                className="border border-mono-300 bg-transparent px-2 py-1 text-xs dark:border-mono-700"
                            />
                            <button
                                type="submit"
                                className={button}
                                disabled={busy || !/^[1-9]\d{0,39}$/.test(query.trim())}
                            >
                                Buscar número
                            </button>
                        </form>
                        {order.bling_order_id && (
                            <button
                                type="button"
                                className={button}
                                disabled={busy}
                                onClick={() =>
                                    act(async () => {
                                        await api.unlinkPedidoBlingOrder(orderId);
                                        setResult(null);
                                        await onChanged();
                                    })
                                }
                            >
                                Desvincular
                            </button>
                        )}
                    </div>
                    {busy && <output className="text-xs">Consultando…</output>}
                    {error && (
                        <p role="alert" className="text-xs text-danger-600">
                            {error}
                        </p>
                    )}
                    {result?.status === 'not_found' && (
                        <p className="text-xs">
                            Nenhum pedido Bling com o número deste pedido ou pack no canal. Confira essa referência no
                            Bling e tente novamente.
                        </p>
                    )}
                    {result?.status === 'linked' && (
                        <output className="text-xs">
                            Pedido Bling {result.link.bling_order_number || result.link.bling_order_id} vinculado.
                        </output>
                    )}
                    {result?.candidates?.length > 0 && (
                        <ul className="divide-y divide-mono-100 dark:divide-mono-800">
                            {result.candidates.map((candidate) => (
                                <li
                                    key={candidate.bling_order_id}
                                    className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs"
                                >
                                    <span>
                                        Pedido <strong>{candidate.bling_order_number}</strong> · canal{' '}
                                        {candidate.store_order_number} · loja {candidate.store_id || '—'}
                                        {candidate.buyer_name ? ` · ${candidate.buyer_name}` : ''}
                                        {candidate.total !== null ? ` · ${FormatMoney(candidate.total)}` : ''}
                                    </span>
                                    <button
                                        type="button"
                                        className={button}
                                        disabled={busy || order.bling_order_id === candidate.bling_order_id}
                                        onClick={() => link(candidate.bling_order_id)}
                                    >
                                        {order.bling_order_id === candidate.bling_order_id
                                            ? 'Vinculado'
                                            : 'Vincular este pedido'}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
}
